import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import qs from "qs";

import { setDisableBtn, toggleDeleteDialog, toggleDialog } from "../redux/Slices/Global/globalSlice";
import { useDataTableContext } from "../pages/Contexts/DataTableContext";
import { useCustomToast } from "../pages/Contexts/ToastContext";
import axiosInstance from "../config/Axios";
import { handleResErrors } from "./utils";
import { PLAYLISTS_TABLE_CONFIG } from "../Constants/datatables";
import { setPlaylistDetails, setPlaylistFetchingLoading } from "../redux/Slices/playlistSlice";

//--------------------------Create Playlist--------------------------
const createPlaylist = async (payload) => {
    const { data } = await axiosInstance.post(`/playlist/create`, payload);
    return data.data;
}
export const useCreatePlaylistMutation = () => {
    const { showToast } = useCustomToast();
    const navigate = useNavigate();
    const dispatch = useDispatch()
    const queryClient = useQueryClient();

    return useMutation(createPlaylist, {
        onSuccess: async (data) => {
            dispatch(toggleDialog("createPlaylist"));
            queryClient.invalidateQueries({ queryKey: ["companyPlaylists"] });
            showToast('success', 'PlayList Creation', 'PlayList Created Successfully');
            navigate('/playlist/' + data?.id + '/show')
        },
        onError: (error) => handleResErrors(error, showToast)
    })
}

//--------------------------Update Playlist--------------------------
const updatePlaylist = async (payload) => {
    const { data } = await axiosInstance.put(`/playlist/${payload.id}`, payload.data);
    return data.data;
}

export const useUpdatePlaylistMutation = () => {
    const { showToast } = useCustomToast();
    const queryClient = useQueryClient();

    return useMutation(updatePlaylist,
        {
            onSuccess: async (data) => {
                queryClient.invalidateQueries({ queryKey: ["companyPlaylists"] });

            },
            onError: (error) => handleResErrors(error, showToast)
        })
}

// -------------------------Delete Playlist-------------------------//
const deletePlaylist = async (payload) => {
    const { data } = await axiosInstance.delete(`/playlist/${payload.id}/delete`);
    return data.data;
}

export const useDeletePlaylist = () => {
    const { setLazyParams } = useDataTableContext();
    const { showToast } = useCustomToast();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    return useMutation(deletePlaylist, {
        onSuccess: async () => {
            dispatch(toggleDeleteDialog("playlist"));
            showToast('success', 'Delete Playlist', 'PlayList Deleted Successfully');
            queryClient.invalidateQueries({ queryKey: ["companyPlaylists"] });
        },
        onError: (error) => {
            handleResErrors(error, showToast)
            dispatch(setDisableBtn(false))
        }
    })
}

//--------------------------Get Playlist By ID--------------------------
const getPlaylist = async (id) => {
    const { data } = await axiosInstance.get(`/playlist/${id}`);
    return data.data;
}

export const useGetPlaylist = (id) => {
    const { showToast } = useCustomToast();
    const dispatch = useDispatch()
    const { isFetching: playlistFetching, isLoading: playlistLoading, data: playlistInfo, refetch: refetchPlaylist } = useQuery(
        ['getPlaylist', id],
        () => getPlaylist(id),
        {
            enabled: !!id,
            onSuccess: async (res, variables) => {
                dispatch(setPlaylistFetchingLoading(false))
                dispatch(setPlaylistDetails(res))


            },
            onError: (error) => {
                showToast("error", "Fetching Data", error.response?.data?.message);
            },
        }
    );

    return { playlistLoading, playlistInfo, refetchPlaylist, playlistFetching };
}

//--------------------------Get Playlist Content by layer--------------------------
const getPlaylistLayerContent = async ({ queryKey }) => {
    const [, id, layer] = queryKey
    const { data } = await axiosInstance.get(`/playlist/${id}/${layer}/media`);
    return data.data;
}

export const useGetPlaylistLayerContent = (id, layer) => {
    const { showToast } = useCustomToast();

    // isError/error: additive — needed by the campaign-builder playlist
    // accordion (PlaylistAccordionRow.jsx) to show its own inline
    // error+retry state (spec item A) instead of silently showing an empty
    // list. Existing callers that destructure only {isLoading, data, refetch}
    // are unaffected.
    const { isLoading, data, isError, error, refetch } = useQuery(
        ['getPlaylistLayerContent', id, layer],
        getPlaylistLayerContent,
        {
            enabled: !!id,
            keepPreviousData: false,  // Don't keep old data to show fresh updates immediately
            staleTime: 0,  // Always refetch when invalidated for real-time updates
            cacheTime: 1000 * 60 * 2,  // Keep in cache for 2 minutes
            onError: (error) => {
                showToast("error", "Fetching Data", error.response?.data?.message);
            },
        }
    );

    return { isLoading, data, isError, error, refetch };
}

//--------------------------Playlist Campaign--------------------------
const publishPlaylist = async (payload) => {
    const { data } = await axiosInstance.post(`/playlist/${payload.id}/publish`, {});
    return data.data;
}

export const usePublishPlaylistMutation = () => {
    const { showToast } = useCustomToast();
    const updatePlaylist = useUpdatePlaylistMutation();
    const queryClient = useQueryClient();

    return useMutation(publishPlaylist, {
        onSuccess: async (res, variables) => {
            await updatePlaylist.mutateAsync({
                id: variables.id,
                data: {
                    status: "ACTIVE",
                    name: variables?.name,
                },
            }).then(() => queryClient.invalidateQueries(['getPlaylist', variables.id])
            )
            queryClient.invalidateQueries({ queryKey: ["companyPlaylists"] });

        },
        onError: (error) => handleResErrors(error, showToast)
    })
}

//--------------------------Playlist rese--------------------------
const resetPlaylist = async (payload) => {
    const { data } = await axiosInstance.post(`/playlist/${payload.id}/reset`, {});
    return data.data;
}

export const useResetPlaylistMutation = () => {
    const { showToast } = useCustomToast();
    const updatePlaylist = useUpdatePlaylistMutation();
    const queryClient = useQueryClient();

    return useMutation(resetPlaylist, {
        onSuccess: async (res, variables) => {
            await updatePlaylist.mutateAsync({
                id: variables.id,
                data: {
                    status: "ACTIVE",
                    name: variables?.name,
                },
            }).then(() => {
                queryClient.invalidateQueries(['getPlaylist', variables.id])
                queryClient.invalidateQueries(['getPlaylistLayerContent', variables.id, "MAIN"])
                queryClient.invalidateQueries({ queryKey: ["companyPlaylists"] });

            }

            )
        },
        onError: (error) => handleResErrors(error, showToast)
    })
}


//--------------------------Fetcher for company playlists--------------------------

const getCompanyPlaylists = async ({ pageParam = 1, queryKey }) => {
    const [_key, { per_page, search, tags }] = queryKey;

    // Ensure it's an array of IDs (not objects)
    const tagIds = Array.isArray(tags)
        ? tags.map(t => (typeof t === "object" ? t.id : t))
        : [];

    const { data } = await axiosInstance.get("/playlist/company", {
        params: {
            page: pageParam,
            per_page,
            search: search?.trim() || undefined,
            tags: tagIds.length ? tagIds : undefined,
        },
        paramsSerializer: params =>
            qs.stringify(params, {
                arrayFormat: "brackets",
                encodeValuesOnly: true,
                skipNulls: true,
            }),
    });

    return {
        items: data.data,
        currentPage: data.pagination.current_page,
        totalItems: data.pagination.total,
        totalPages: data.pagination.total_pages,
    };
};

/**
 * React Query infinite hook
 */
export const useCompanyPlaylists = ({
    per_page = 10,
    search = "",
    tags = [],
}) => {
    const dispatch = useDispatch();
    const { showToast } = useCustomToast?.() || { showToast: () => { } };

    return useInfiniteQuery(
        ["companyPlaylists", { per_page, search, tags }],
        getCompanyPlaylists,
        {
            getNextPageParam: (lastPage) => {
                if (lastPage.currentPage < lastPage.totalPages) {
                    return lastPage.currentPage + 1;
                }
                return undefined;
            },
            keepPreviousData: true,
            staleTime: 1000 * 60 * 5,
            refetchOnWindowFocus: false,

            onMutate: () => {
                // dispatch(setIsLoading(true));
            },
            onSettled: () => {
                // dispatch(setIsLoading(false));
            },
            onError: (err) => {
                showToast?.({
                    severity: "error",
                    summary: "Failed to load playlists",
                    detail: err?.response?.data?.message || err.message,
                });
            },
        }
    );
};